import React, { useEffect, useMemo, useRef, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { ChevronDown, ChevronLeft, Clock3, Info, MapPin, Search } from 'lucide-react';
import { fetchLimaDistrictCatalog } from '../../../utils/locationCatalog';
import {
  buildIllustrativeDistricts,
  getDistrictDeliveryStart,
  searchCoverageDistricts,
} from './deliveryCoverage';

const MAP_TOKEN = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN;
const INITIAL_CENTER = [-77.045, -12.075];
const ALL_DISTRICTS_BOUNDS = [[-77.25, -12.54], [-76.62, -11.72]];

export function DistrictDropdown({ districts, selectedDistrict, onSelect }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const containerRef = useRef(null);
  const searchRef = useRef(null);
  const matches = useMemo(() => searchCoverageDistricts(districts, query), [districts, query]);

  useEffect(() => {
    if (!open) return undefined;
    searchRef.current?.focus();
    const closeOutside = (event) => {
      if (!containerRef.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener('pointerdown', closeOutside);
    return () => document.removeEventListener('pointerdown', closeOutside);
  }, [open]);

  function choose(district) {
    onSelect(district);
    setOpen(false);
    setQuery('');
    setActiveIndex(0);
  }

  function onSearchKeyDown(event) {
    if (event.key === 'Escape') {
      setOpen(false);
      return;
    }
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((current) => {
        if (!matches.length) return 0;
        return (current + (event.key === 'ArrowDown' ? 1 : -1) + matches.length) % matches.length;
      });
    }
    if (event.key === 'Enter' && matches[activeIndex]) {
      event.preventDefault();
      choose(matches[activeIndex]);
    }
  }

  return (
    <div className="relative" ref={containerRef}>
      <label id="coverage-district-label" className="mb-2 block text-sm font-bold text-slate-800">
        Busca un distrito
      </label>
      <button
        type="button"
        aria-label={`Buscar distrito. ${selectedDistrict?.name || 'Ninguno seleccionado'}`}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls="coverage-district-options"
        onClick={() => setOpen((value) => !value)}
        className="flex w-full items-center gap-3 rounded-xl border border-slate-300 bg-white px-4 py-3 text-left shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-[#B1336E]"
      >
        <Search className="h-5 w-5 shrink-0 text-[#B1336E]" aria-hidden="true" />
        <span className={`min-w-0 flex-1 truncate ${selectedDistrict ? 'font-semibold text-slate-900' : 'text-slate-500'}`}>
          {selectedDistrict?.name || 'Selecciona o busca un distrito'}
        </span>
        <ChevronDown className={`h-4 w-4 shrink-0 text-slate-500 transition-transform ${open ? 'rotate-180' : ''}`} aria-hidden="true" />
      </button>
      {open && (
        <div className="absolute z-30 mt-2 w-full overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl">
          <div className="border-b border-slate-200 p-2">
            <input
              ref={searchRef}
              type="search"
              value={query}
              onChange={(event) => { setQuery(event.target.value); setActiveIndex(0); }}
              onKeyDown={onSearchKeyDown}
              aria-label="Buscar distrito"
              aria-controls="coverage-district-options"
              aria-activedescendant={matches[activeIndex] ? `coverage-option-${matches[activeIndex].slug}` : undefined}
              placeholder="Escribe un distrito..."
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#B1336E]"
            />
          </div>
          <div id="coverage-district-options" role="listbox" aria-label="Distritos con cobertura" className="max-h-64 overflow-y-auto p-1">
            {matches.length ? matches.map((district, index) => (
              <button
                id={`coverage-option-${district.slug}`}
                key={district.slug}
                type="button"
                role="option"
                aria-selected={selectedDistrict?.slug === district.slug}
                onMouseEnter={() => setActiveIndex(index)}
                onClick={() => choose(district)}
                className={`flex w-full items-center justify-between gap-3 rounded-lg px-3 py-2.5 text-left text-sm ${index === activeIndex ? 'bg-[#B1336E]/10' : 'hover:bg-slate-50'}`}
              >
                <span className="font-semibold text-slate-800">{district.name}</span>
                <span className="shrink-0 text-xs font-bold text-[#B1336E]">Desde {getDistrictDeliveryStart(district)?.label}</span>
              </button>
            )) : (
              <p className="px-3 py-4 text-sm text-slate-500">No encontramos ese distrito.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function DeliveryCoverageMap({ onBack }) {
  const [districts, setDistricts] = useState([]);
  const [catalogState, setCatalogState] = useState('loading');
  const [retryCount, setRetryCount] = useState(0);
  const [selectedSlug, setSelectedSlug] = useState(null);
  const [mapError, setMapError] = useState(false);
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const mapLoadedRef = useRef(false);
  const selectRef = useRef(null);
  const selectedSlugRef = useRef(null);

  useEffect(() => {
    let active = true;
    setCatalogState('loading');
    fetchLimaDistrictCatalog()
      .then((items) => {
        if (!active) return;
        const covered = items.filter((district) => getDistrictDeliveryStart(district));
        setDistricts(covered.sort((a, b) => a.name.localeCompare(b.name, 'es')));
        setCatalogState(covered.length ? 'ready' : 'error');
      })
      .catch(() => { if (active) setCatalogState('error'); });
    return () => { active = false; };
  }, [retryCount]);

  const selectedDistrict = districts.find((district) => district.slug === selectedSlug) || null;
  const mapData = useMemo(() => buildIllustrativeDistricts(districts), [districts]);
  selectRef.current = (slug) => setSelectedSlug(slug);
  selectedSlugRef.current = selectedSlug;

  useEffect(() => {
    if (catalogState !== 'ready' || !MAP_TOKEN || !mapContainerRef.current || mapRef.current) return undefined;
    mapboxgl.accessToken = MAP_TOKEN;
    let map;
    try {
      map = new mapboxgl.Map({
        container: mapContainerRef.current,
        style: 'mapbox://styles/mapbox/light-v11',
        center: INITIAL_CENTER,
        zoom: 10,
        minZoom: 8,
        maxZoom: 16,
      });
    } catch (error) {
      setMapError(true);
      return undefined;
    }
    mapRef.current = map;
    map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), 'top-right');
    map.on('load', () => {
      mapLoadedRef.current = true;
      map.addSource('coverage-districts', { type: 'geojson', data: mapData });
      map.addLayer({
        id: 'coverage-areas', type: 'fill', source: 'coverage-districts',
        paint: {
          'fill-color': ['case', ['get', 'afternoonOnly'], '#B1336E', '#67A43C'],
          'fill-opacity': 0.38,
        },
      });
      map.addLayer({
        id: 'coverage-divisions', type: 'line', source: 'coverage-districts',
        paint: { 'line-color': '#ffffff', 'line-width': 1.5, 'line-opacity': 0.9 },
      });
      map.addLayer({
        id: 'coverage-selected', type: 'line', source: 'coverage-districts',
        filter: ['==', ['get', 'slug'], ''],
        paint: { 'line-color': '#14213d', 'line-width': 3.5 },
      });
      map.addLayer({
        id: 'coverage-labels', type: 'symbol', source: 'coverage-districts', minzoom: 11,
        layout: { 'text-field': ['get', 'name'], 'text-size': 11, 'text-font': ['Open Sans Semibold'], 'text-max-width': 8 },
        paint: { 'text-color': '#172033', 'text-halo-color': '#ffffff', 'text-halo-width': 1.5 },
      });
      if (selectedSlugRef.current) {
        map.setFilter('coverage-selected', ['==', ['get', 'slug'], selectedSlugRef.current]);
        const selected = districts.find((district) => district.slug === selectedSlugRef.current);
        if (selected?.longitude != null && selected?.latitude != null) {
          map.flyTo({ center: [Number(selected.longitude), Number(selected.latitude)], zoom: 12, essential: true });
        }
      }
      map.on('click', 'coverage-areas', (event) => {
        const slug = event.features?.[0]?.properties?.slug;
        if (slug) selectRef.current?.(slug);
      });
      map.on('mouseenter', 'coverage-areas', () => { map.getCanvas().style.cursor = 'pointer'; });
      map.on('mouseleave', 'coverage-areas', () => { map.getCanvas().style.cursor = ''; });
    });
    map.on('error', (event) => {
      if (!mapLoadedRef.current && event.error) setMapError(true);
    });
    return () => {
      mapLoadedRef.current = false;
      mapRef.current = null;
      map.remove();
    };
  }, [catalogState, districts, mapData]);

  useEffect(() => {
    const map = mapRef.current;
    if (!mapLoadedRef.current || !map?.getLayer('coverage-selected')) return;
    map.setFilter('coverage-selected', ['==', ['get', 'slug'], selectedSlug || '']);
    if (selectedDistrict?.longitude != null && selectedDistrict?.latitude != null) {
      map.flyTo({ center: [Number(selectedDistrict.longitude), Number(selectedDistrict.latitude)], zoom: 12, essential: true });
    }
  }, [selectedDistrict, selectedSlug]);

  const selectedStart = getDistrictDeliveryStart(selectedDistrict);

  return (
    <div className="min-h-screen bg-slate-50 font-montserrat">
      <div className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-6xl items-center gap-3 px-4 py-4 sm:px-6">
          <button type="button" onClick={onBack} aria-label="Volver al menú" className="rounded-xl p-2 text-slate-600 hover:bg-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#B1336E]">
            <ChevronLeft className="h-6 w-6" />
          </button>
          <div>
            <h1 className="text-xl font-extrabold text-slate-900 sm:text-2xl">Mapa de horarios</h1>
            <p className="text-sm text-slate-500">Conoce desde qué hora entregamos en cada distrito.</p>
          </div>
        </div>
      </div>

      <main className="mx-auto grid max-w-6xl gap-5 px-4 py-5 sm:px-6 lg:grid-cols-[340px_minmax(0,1fr)] lg:py-8">
        <div className="space-y-4">
          <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
            {catalogState === 'loading' && <p className="text-sm text-slate-600">Cargando distritos...</p>}
            {catalogState === 'error' && (
              <div role="alert" className="space-y-3 text-sm text-slate-700">
                <p>No pudimos cargar los horarios por distrito. No mostraremos horarios sin confirmar.</p>
                <button type="button" onClick={() => setRetryCount((count) => count + 1)} className="font-bold text-[#B1336E] underline">Reintentar</button>
              </div>
            )}
            {catalogState === 'ready' && (
              <DistrictDropdown districts={districts} selectedDistrict={selectedDistrict} onSelect={(district) => setSelectedSlug(district.slug)} />
            )}
          </section>

          <section aria-live="polite" className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            {selectedDistrict ? (
              <>
                <div className="flex items-center gap-2 text-sm font-bold text-slate-600"><MapPin className="h-4 w-4" />{selectedDistrict.name}</div>
                <p className="mt-3 text-sm text-slate-500">Entregas desde</p>
                <p className="mt-1 text-3xl font-extrabold text-[#B1336E]">{selectedStart?.label}</p>
                <p className="mt-3 text-xs leading-5 text-slate-500">Este es el inicio habitual de entregas en el distrito, no una hora de llegada garantizada ni un horario seleccionable.</p>
              </>
            ) : (
              <div className="flex items-start gap-3 text-sm text-slate-600">
                <Clock3 className="mt-0.5 h-5 w-5 shrink-0 text-[#B1336E]" />
                <p>Selecciona un distrito en el mapa o búscalo arriba para consultar su hora de inicio.</p>
              </div>
            )}
          </section>

          <section className="rounded-2xl border border-slate-200 bg-white p-4 text-sm shadow-sm">
            <h2 className="mb-3 font-bold text-slate-800">Guía de horarios</h2>
            <div className="space-y-2">
              <div className="flex items-center gap-2"><span className="h-4 w-4 rounded bg-[#67A43C]" /> <span>Desde las 11:00 a. m.</span></div>
              <div className="flex items-center gap-2"><span className="h-4 w-4 rounded bg-[#B1336E]" /> <span>Desde la 1:00 p. m.</span></div>
            </div>
            <p className="mt-4 flex gap-2 text-xs leading-5 text-slate-500"><Info className="mt-0.5 h-4 w-4 shrink-0" /> Las divisiones del mapa son ilustrativas. Confirma el horario por nombre de distrito; la ruta y el tráfico pueden variar.</p>
          </section>
        </div>

        <section aria-label="Mapa ilustrativo de distritos de Lima y Callao" className="relative min-h-[440px] overflow-hidden rounded-2xl border border-slate-200 bg-slate-100 shadow-sm lg:min-h-[670px]">
          {MAP_TOKEN && catalogState === 'ready' ? (
            <>
              <div ref={mapContainerRef} className="absolute inset-0" />
              {mapError ? (
                <div className="absolute inset-0 flex items-center justify-center bg-slate-100 p-8 text-center text-sm text-slate-600">
                  El mapa no está disponible ahora. Puedes consultar cada distrito con el buscador.
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => mapRef.current?.fitBounds(ALL_DISTRICTS_BOUNDS, { padding: 32, duration: 500 })}
                  className="absolute bottom-5 left-5 z-10 rounded-xl bg-white px-3 py-2 text-xs font-bold text-slate-800 shadow-md hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#B1336E]"
                >
                  Ver toda la cobertura
                </button>
              )}
            </>
          ) : (
            <div className="flex h-full min-h-[440px] items-center justify-center p-8 text-center text-sm text-slate-600">
              {catalogState === 'loading' ? 'Cargando mapa...' : 'El mapa no está disponible ahora. Puedes consultar cada distrito con el buscador.'}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
